/**
 * useFormAccess Hook
 *
 * Manages form state and submission for Step 3 form access.
 * Mirrors the pattern from Step 2 (useIdentityVerification).
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { FormAccessRequestData } from '../types/api.types';
import { postFormAccess, FormAccessResult } from '../services/formAccessService';
import {
  validateFirstNameFormat,
  validateLastNameFormat,
  validateSSN4Format,
  validateEmailFormat,
  validateEmailMatch
} from '../utils/validators';

/** Field error state for all form fields */
interface FieldErrors {
  firstName: string | null;
  lastName: string | null;
  ssn4: string | null;
  email: string | null;
  emailVerify: string | null;
}

const EMPTY_ERRORS: FieldErrors = {
  firstName: null,
  lastName: null,
  ssn4: null,
  email: null,
  emailVerify: null
};

export function useFormAccess(sessionToken: string) {
  const navigate = useNavigate();
  const { formAccessId } = useSession();

  // ── Form Fields ────────────────────────────────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [ssn4, setSsn4] = useState('');
  const [email, setEmail] = useState('');
  const [emailVerify, setEmailVerify] = useState('');
  const [showSSN, setShowSSN] = useState(false);

  // ── Error & Loading State ──────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(EMPTY_ERRORS);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FormAccessResult | null>(null);

  /**
   * Validate all form fields at once.
   * Returns true if all validations pass.
   */
  const validateAll = useCallback((): boolean => {
    const errors: FieldErrors = {
      firstName: validateFirstNameFormat(firstName),
      lastName: validateLastNameFormat(lastName),
      ssn4: validateSSN4Format(ssn4),
      email: validateEmailFormat(email),
      emailVerify: validateEmailMatch(email, emailVerify)
    };

    setFieldErrors(errors);
    return Object.values(errors).every(err => err === null);
  }, [firstName, lastName, ssn4, email, emailVerify]);

  /**
   * Validate a single field on blur.
   * Updates error state for that field only.
   * Note: emailVerify validation depends on email field value.
   */
  const handleFieldBlur = useCallback((field: keyof FieldErrors) => {
    if (field === 'emailVerify') {
      // Email verification depends on email value
      const error = validateEmailMatch(email, emailVerify);
      setFieldErrors(prev => ({ ...prev, emailVerify: error }));
    } else {
      const validators: Record<Exclude<keyof FieldErrors, 'emailVerify'>, (v: string) => string | null> = {
        firstName: validateFirstNameFormat,
        lastName: validateLastNameFormat,
        ssn4: validateSSN4Format,
        email: validateEmailFormat
      };

      const value = field === 'firstName' ? firstName
        : field === 'lastName' ? lastName
        : field === 'ssn4' ? ssn4
        : email;

      const error = validators[field](value);
      setFieldErrors(prev => ({ ...prev, [field]: error }));
    }
  }, [firstName, lastName, ssn4, email, emailVerify]);

  /**
   * Submit form to backend.
   *
   * Stage 1: Client-side format validation
   * Stage 2: Server-side database validation (combined query + SSN check)
   */
  const submit = useCallback(async () => {
    if (!sessionToken) {
      navigate('/error?reason=NO_SESSION', { replace: true });
      return;
    }

    // Stage 1: Validate all formats
    const isValid = validateAll();
    /* console.log('✅ Validation result:', isValid); 
    console.log('❌ Field errors:', fieldErrors);*/

    if (!isValid) {
      /* console.log('🚫 Validation failed, stopping submission'); */
      return;  // Errors already set, user sees inline messages
    }

    // Stage 2: Submit to server
    /* console.log('🚀 Starting API call...'); */
    setResult(null);
    setLoading(true);

    const request: FormAccessRequestData = {
      firstName: firstName.trim(),
      lastName: lastName.trim() || undefined,
      last4SSN: ssn4,
      email: email.trim() || undefined
    };

    /* console.log('📤 API request payload:', request); */

    try {
      const response = await postFormAccess(request, sessionToken, formAccessId);
      /* console.log('📥 API response:', response); */
      setResult(response);
    } catch (error) {
      /* console.error('💥 API call error:', error); */

      // Handle session expiration from API response
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        /* console.log('🔄 Session expired during API call, redirecting'); */
        navigate('/error?reason=SESSION_EXPIRED', { replace: true });
        return;
      }
    } finally {
      setLoading(false);
    }

  }, [firstName, lastName, ssn4, email, sessionToken, validateAll, navigate]);

  return {
    // ── Form State ─────────────────────────────────────────────────────
    firstName,
    setFirstName,
    lastName,
    setLastName,
    ssn4,
    setSsn4,
    email,
    setEmail,
    emailVerify,
    setEmailVerify,
    showSSN,
    setShowSSN,

    // ── Validation State ───────────────────────────────────────────────
    fieldErrors,
    loading,
    result,

    // ── Actions ────────────────────────────────────────────────────────
    handleFieldBlur,
    submit
  };
}
